import {
  FIELD_CONTROLS,
  type CollectionManifest,
  type FieldManifest,
  type Mode,
} from "../../../shared/api/generated/contracts";
import type { SelectOption } from "../../../shared/ui";

/**
 * The render dictionary's own axis. It is not the app `Mode`: a record can be
 * read in `published` mode and edited in the same mode, and `print` is the only
 * app mode that forces one field mode outright.
 */
export type FieldMode = "display" | "edit" | "print";

export function fieldModeFor(mode: Mode, editing: boolean): FieldMode {
  if (mode === "print") return "print";
  return editing ? "edit" : "display";
}

/** The ten controls of the UI Kit render dictionary, across nine field types. */
export type FieldControl =
  | "text_input"
  | "textarea"
  | "number_input"
  | "checkbox"
  | "switch"
  | "date_picker"
  | "time_picker"
  | "datetime_picker"
  | "select"
  | "multi_select"
  | "reference_select";

export const FIELD_CONTROL_KINDS: readonly FieldControl[] = [
  "text_input",
  "textarea",
  "number_input",
  "checkbox",
  "switch",
  "date_picker",
  "time_picker",
  "datetime_picker",
  "select",
  "multi_select",
  "reference_select",
];

/**
 * A control is only renderable when the compiled manifest pairs it with a field
 * type that declares it. Anything else is an unsupported manifest, treated the
 * same way an unregistered widget kind is.
 */
export function isSupportedControl(field: FieldManifest): boolean {
  return (
    FIELD_CONTROL_KINDS.includes(field.control as FieldControl) &&
    FIELD_CONTROLS[field.type].includes(field.control)
  );
}

export interface FieldRenderProps {
  field: FieldManifest;
  value: unknown;
  mode: FieldMode;
  /** Server-authoritative diagnostic for this field. */
  error?: string;
  onChange?: (value: unknown) => void;
  labels: FieldLabels;
  /** Element id, supplied by Field so label/control stay associated. */
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

export interface FieldLabels {
  choose: string;
  chooseDate: string;
  chooseTime: string;
  remove: string;
  empty: string;
}

/** Declared options for select-like controls, read out of the manifest. */
export function optionsOf(field: FieldManifest): SelectOption[] {
  const raw = field.config?.options;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): SelectOption[] => {
    if (typeof entry === "string") return [{ value: entry, label: entry }];
    if (entry && typeof entry === "object") {
      const record = entry as Record<string, unknown>;
      const value = record.value ?? record.id;
      if (typeof value !== "string") return [];
      const label = record.label;
      return [{ value, label: typeof label === "string" ? label : value }];
    }
    return [];
  });
}

export function labelOfOption(field: FieldManifest, value: string): string {
  return (
    optionsOf(field).find((option) => option.value === value)?.label ?? value
  );
}

export function unitOf(field: FieldManifest): string {
  const unit = field.config?.unit;
  return typeof unit === "string" ? unit : "";
}

/**
 * Fields the owner can see and edit. A hidden field (ADR 0198) keeps its
 * stored values but never reaches a form, list, table, detail or print surface.
 */
export function visibleFields(
  collection: Pick<CollectionManifest, "fields"> | undefined,
): FieldManifest[] {
  return (collection?.fields ?? []).filter((field) => field.hidden !== true);
}

/** Ids of the hidden fields, used to strip their keys from record payloads. */
export function hiddenFieldIds(
  collection: Pick<CollectionManifest, "fields"> | undefined,
): ReadonlySet<string> {
  return new Set(
    (collection?.fields ?? [])
      .filter((field) => field.hidden === true)
      .map((field) => field.id),
  );
}

export function withoutKeys(
  payload: Readonly<Record<string, unknown>>,
  keys: ReadonlySet<string>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(payload).filter(([key]) => !keys.has(key)),
  );
}
